import { z } from 'zod';
import { Confidence, GeoPoint, Id, Rupiah } from './primitives.js';
/**
 * A rupiah amount a user types into a form.
 *
 * The ceiling is `int4`, which is what every rupiah column in the schema
 * actually is. Without it `z.number().int().positive()` accepts 99,999,999,999,
 * the request passes validation, and PostgreSQL rejects it as "integer out of
 * range" — a 500 for what is plainly a typo, on a field that now carries a
 * thousands-separator mask and so makes a long number easy to produce by
 * holding a key down.
 *
 * Rp 2.1 billion for a single SKU's reference price is already far beyond any
 * real figure, so this bounds a mistake rather than a use case.
 */
export const RupiahAmount = z.number().int().positive().max(2_147_483_647);
/** FR-05 / FR-12: every SKU is one of ours or a competitor's. */
export const SkuType = z.enum(['INTERNAL', 'KOMPETITOR']);
/**
 * Which sales division owns an internal product.
 *
 * REQUIRED ON INTERNAL, FORBIDDEN ON KOMPETITOR — the same shape as the shelf
 * plan below, and stated in the same three places: here, as
 * `assertDivisionMatchesType` in the masterdata controller, and as the
 * `skus_division_matches_type` CHECK in migration 0061. A rival's product has no
 * division of ours, so the column is null there rather than carrying a value
 * nobody chose.
 *
 * NOT SCORED YET. It exists ahead of the scoring refactor that will read it, the
 * way `target_row` existed before the formula that grades against it — so the
 * catalogue can be populated and corrected before anything depends on it.
 *
 * NO LABEL MAP, deliberately. `HSJ` and `LKJ` are the words the business uses;
 * expanding them on screen would invent a translation nobody asked for. The same
 * call `IMPORT_ENTITY_LABELS` makes for its codes.
 */
export const SkuDivision = z.enum(['HSJ', 'LKJ']);
export const Sku = z.object({
    id: Id,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    type: SkuType,
    /** Populated for KOMPETITOR SKUs — the Master Kompetitor reference of FR-12. */
    competitorId: Id.nullable(),
    /**
     * Required. Was a free `z.string().max(100).nullable()` until the category
     * master landed — see `SkuCategory`. "Uncategorised" is still expressible, as
     * the "Tanpa Kategori" row, which is a value a report can name rather than a
     * null it has to decide what to do with.
     */
    categoryId: Id,
    /**
     * Required, and the same argument as `categoryId` above: an optional grouping
     * is a grouping nobody fills in. "Ungrouped" is expressible as the "Tanpa
     * Grouping" row migration 0054 creates — a value a report can name rather than
     * a null it has to decide what to do with.
     */
    groupingId: Id,
    /**
     * The reference price. DORMANT — retained, not read.
     *
     * This was the Plan half of the FR-14 price index (`observed / targetPriceIdr
     * × 100`). That metric is retired: the index is defined by `CompetitionPlan`
     * alone. The column keeps its stored values and the write schemas below still
     * accept it, but no console surface, no import and no report touches it —
     * which is what makes re-enabling the field a UI-only change.
     */
    targetPriceIdr: Rupiah.nullable(),
    /** Null on KOMPETITOR, set on INTERNAL — see `SkuDivision`. */
    division: SkuDivision.nullable(),
    active: z.boolean(),
});
export const Competitor = z.object({
    id: Id,
    name: z.string().min(1).max(200),
    active: z.boolean(),
});
/**
 * FR-12 SKU category master.
 *
 * `Sku.category` was a free string until this existed, which meant the category
 * axis of every report was whatever operators had typed — three spellings of
 * "Minuman" are three rows in a GROUP BY and one line of business split into
 * three. Name only, no code: nothing outside this system quotes a category
 * identifier, so a second key would only be something the importer could
 * disagree with itself about.
 */
export const SkuCategory = z.object({
    id: Id,
    name: z.string().min(1).max(100),
    active: z.boolean(),
});
/* -------------------------------------------------------------------------- */
/* SKU reference images                                                       */
/* -------------------------------------------------------------------------- */
/**
 * The four sides a SKU is photographed from.
 *
 * Four rather than "as many as you like" because the set is what makes the
 * catalogue comparable: a SKU with one photo and a SKU with nine are not two
 * points on a quality scale anyone can act on, whereas "3 of 4" is. RSK-05 asks
 * the client for an image-count-per-SKU acceptance threshold, and a threshold
 * needs a denominator.
 */
export const SkuSide = z.enum(['FRONT', 'BACK', 'LEFT', 'RIGHT']);
export const SkuReferenceImage = z.object({
    id: Id,
    skuId: Id,
    side: SkuSide,
    objectKey: z.string().min(1),
    contentType: z.string().min(1),
    /**
     * Whether this image has an embedding yet.
     *
     * The vector itself never crosses this boundary — it is 512 floats of no use
     * to a browser, and shipping it would put ~2 kB of noise into every row of a
     * list. What the console needs is whether the SKU is searchable by image yet,
     * which is one bit.
     */
    embedded: z.boolean(),
    /** Short-lived presigned GET. Absent on write responses. */
    viewUrl: z.string().url().nullable(),
});
/**
 * Step 1 of the upload: ask for somewhere to put the image.
 *
 * Same three-step shape as a visit photo (`photo.ts`) and for the same reason —
 * the image goes straight to object storage and never transits the application
 * tier. `contentLength` is bound into the signature, so the URL cannot be reused
 * to upload something else.
 */
export const PresignSkuImageRequest = z.object({
    side: SkuSide,
    contentType: z.enum(['image/jpeg', 'image/webp', 'image/png']),
    contentLength: z.number().int().positive().max(10 * 1024 * 1024),
});
export const PresignSkuImageResponse = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string().min(1),
    expiresAt: z.string().datetime(),
});
/** Step 3: the upload landed — record it and queue the embedding. */
export const RegisterSkuImageRequest = z.object({
    side: SkuSide,
    objectKey: z.string().min(1),
    contentType: z.string().min(1),
});
export const Outlet = z.object({
    id: Id,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    regionId: Id,
    regionName: z.string(),
    /**
     * Channel and classification, as master data since 0044 — both the id the
     * caller filters and writes by, and the name it displays.
     *
     * The NAME IS DENORMALISED ONTO THE READ MODEL on purpose. Every consumer of
     * an outlet shows the channel and none of them should have to hold a second
     * lookup to render a row: the staff app has no master-data cache at all, and
     * the exports write the name into a cell. The id is the handle, the name is
     * the label, and a rename updates both because both come from the same join.
     *
     * The classification BELONGS TO the channel — see `classificationId` on the
     * outlets table for why that pairing is a database constraint rather than a
     * convention. Nothing that reads this pair needs to re-check it.
     */
    channelId: Id,
    channel: z.string().max(100),
    classificationId: Id,
    classification: z.string().max(100),
    address: z.string().max(500).nullable(),
    /**
     * Master coordinate, compared against device GPS at check-in and check-out.
     * TAD 8 records that FR-03 tolerance checking and FR-16 signal quality both
     * depend on this being accurate — it is client-supplied and not independently
     * verifiable by the platform.
     *
     * NULLABLE, because an outlet is often registered before anyone has surveyed
     * it. A guessed coordinate is worse than none: check-in measures against it,
     * reports OUT_OF_TOLERANCE, and FR-16 raises GPS_OUT_OF_TOLERANCE against a
     * salesperson who did nothing wrong. Null instead means the radius check is
     * skipped and the visit says so (`NOT_CONFIGURED`).
     */
    location: GeoPoint.omit({ accuracyM: true }).nullable(),
    /** FR-03: configurable per outlet, default 100 m. */
    gpsToleranceM: z.number().int().positive().default(100),
    /**
     * Whether the shop has a chiller — an outlet capability, added in 0053.
     *
     * NOT NULLABLE, and there is no "unknown". See the column comment on
     * `outlets.have_chiller` for why the tri-state `location` needs would be wrong
     * here. The practical consequence for a reader: `false` on an outlet that
     * predates the migration means "no record of one", not "somebody checked".
     */
    haveChiller: z.boolean(),
    active: z.boolean(),
});
/** An outlet as shown in the FR-02 list — carries the computed distance. */
export const OutletListItem = Outlet.extend({
    distanceM: z.number().nonnegative().nullable(),
});
/**
 * FR-12 bulk import result.
 *
 * TSP 2.2.3: the transaction must roll back and report the offending row —
 * never a partial import. `imported` is therefore 0 whenever `errors` is
 * non-empty.
 */
export const ImportResult = z.object({
    /**
     * `PLANOGRAM` is retained with no importer behind it. The planogram master is
     * gone — its targets are SKU columns now and arrive through the SKU import —
     * but `import_batches` rows recorded before that still carry the value, and
     * removing it from the enum would mean rebuilding a PostgreSQL type to erase
     * history nobody asked to lose. `POST /masterdata/import/PLANOGRAM` is
     * rejected by the controller.
     */
    entity: z.enum([
        'SKU',
        'OUTLET',
        'USER',
        'PLANOGRAM',
        'COMPETITOR',
        'REGION',
        'VISIT_TARGET',
        'COMPETITION_PLAN',
    ]),
    totalRows: z.number().int().nonnegative(),
    imported: z.number().int().nonnegative(),
    errors: z.array(z.object({
        row: z.number().int().positive(),
        column: z.string().nullable(),
        message: z.string(),
    })),
});
/** Re-exported for convenience so consumers need one import for master data. */
export const MasterPriceHint = z.object({ skuId: Id, suggestedIdr: Rupiah, confidence: Confidence });
/* -------------------------------------------------------------------------- */
/* Write requests                                                             */
/* -------------------------------------------------------------------------- */
/**
 * The master-data write payloads.
 *
 * These lived as local `z.object`s inside `masterdata.controller.ts`, which was
 * fine while the server was their only reader. The manager console now builds a
 * form for each one, and a form that disagrees with the validator it posts to is
 * a 400 the user cannot act on. Same schema, both ends.
 *
 * Note `.default()` on several fields: `z.infer` gives the POST-parse shape
 * (defaults applied), `z.input` gives what a caller may legally send. Form state
 * wants `z.input`.
 */
export const UpsertOutletRequest = z
    .object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    regionId: z.string().uuid(),
    /**
     * Ids, not names. The channel was a free string until 0044; both are master
     * rows now, and the pair must be consistent — a classification belongs to
     * exactly one channel. That is checked by a composite foreign key in the
     * database, so this schema does not restate it: a rule enforced in two
     * places drifts, and only one of the two can be the one that is true.
     */
    channelId: z.string().uuid(),
    classificationId: z.string().uuid(),
    address: z.string().max(500).nullable().default(null),
    /** ALAMAT KIRIM (0065). Optional, and never copied from `address`. */
    deliveryAddress: z.string().max(500).nullable().default(null),
    /**
     * Optional as a PAIR. An outlet with no coordinate is a legitimate state —
     * see `Outlet.location` — but half a coordinate is not, so both fields
     * default to null together and the refine below keeps them that way.
     */
    lat: z.number().min(-90).max(90).nullable().default(null),
    lng: z.number().min(-180).max(180).nullable().default(null),
    gpsToleranceM: z.number().int().positive().default(100),
    /**
     * Defaulted rather than required, matching the database column. A create
     * that says nothing about the chiller means "no record of one" — the same
     * thing every pre-0053 row says — and refusing the request instead would
     * make an optional capability flag a mandatory field on the create form.
     */
    haveChiller: z.boolean().default(false),
})
    // One PostGIS point behind two fields: a request carrying only a latitude is a
    // typo, not a partial survey, and storing it would put the outlet on the
    // equator.
    .refine((body) => (body.lat === null) === (body.lng === null), {
    message: 'lintang dan bujur harus diisi bersama-sama',
    path: ['lat'],
});
export const UpsertSkuRequest = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    type: SkuType,
    competitorId: z.string().uuid().nullable().default(null),
    /** Required and deliberately without a `.default()` — see `Sku.categoryId`. */
    categoryId: z.string().uuid(),
    /** Required and without a `.default()` for the same reason `categoryId` is. */
    groupingId: z.string().uuid(),
    targetPriceIdr: RupiahAmount.nullable().default(null),
    /**
     * Default null for the reason the shelf plan above defaults null: legal on a
     * KOMPETITOR SKU, illegal on an INTERNAL one, and told so by
     * `assertDivisionMatchesType` rather than by a CHECK arriving as a 500.
     */
    division: SkuDivision.nullable().default(null),
});
/**
 * Partial update of an existing outlet.
 *
 * `code` is deliberately absent. It is the natural key the bulk importer
 * upserts on and the key `POST /masterdata/outlets` conflicts against, so
 * renaming it through a PATCH would silently turn the next import of the old
 * code into a second outlet. Changing a code means creating the replacement and
 * deactivating the original, which is also the honest description of what that
 * operation is.
 */
export const UpdateOutletRequest = z
    .object({
    name: z.string().min(1).max(200),
    regionId: z.string().uuid(),
    /**
     * Sent as a PAIR or not at all — the refine below enforces it.
     *
     * `.partial()` makes every field omittable, which is right for a PATCH but
     * wrong for these two: changing the channel invalidates whatever
     * classification the outlet had, because a classification belongs to exactly
     * one channel. A body that moved the channel and left the classification
     * behind would be asking for a pair the database will refuse — so it is
     * refused here, where the error can name the field, rather than surfacing as
     * a foreign-key violation the console cannot attach to an input.
     */
    channelId: z.string().uuid(),
    classificationId: z.string().uuid(),
    address: z.string().max(500).nullable(),
    deliveryAddress: z.string().max(500).nullable(),
    /**
     * THREE STATES ON THE WIRE, and they are all meaningful:
     *   absent  — leave the stored coordinate alone
     *   null    — clear it (the outlet turns out to be unsurveyed after all)
     *   number  — set it
     * `.partial()` supplies the first, `.nullable()` the second. A PATCH body
     * built by spreading form state must therefore omit the keys it does not
     * mean to touch, rather than sending undefined-shaped nulls.
     */
    lat: z.number().min(-90).max(90).nullable(),
    lng: z.number().min(-180).max(180).nullable(),
    gpsToleranceM: z.number().int().positive(),
    /**
     * Two states here, not the three the coordinate has: absent leaves the flag
     * alone, `true`/`false` sets it. There is no "clear" — the column is NOT
     * NULL, so `false` IS the empty value and a caller who means it sends it.
     */
    haveChiller: z.boolean(),
})
    .partial()
    // `lat` and `lng` are one value in two fields — the database stores a single
    // PostGIS point, so moving an outlet half a degree east is not a thing.
    .refine((body) => (body.lat === undefined) === (body.lng === undefined), {
    message: 'lintang dan bujur harus diisi bersama-sama',
    path: ['lat'],
})
    // And clearing is equally atomic: one null beside one number would ask the
    // database to store a point with a missing axis.
    .refine((body) => (body.lat === null) === (body.lng === null), {
    message: 'lintang dan bujur harus dikosongkan bersama-sama',
    path: ['lat'],
})
    // The channel/classification pair, for the reason given on the fields. Unlike
    // the coordinate there is no "cleared" case to allow: both are NOT NULL, so
    // the only two legal bodies are "neither" and "both".
    .refine((body) => (body.channelId === undefined) === (body.classificationId === undefined), {
    message: 'Channel dan klasifikasi harus diisi bersama-sama',
    path: ['classificationId'],
});
/** Partial update of an existing SKU. `code` is omitted for the same reason. */
export const UpdateSkuRequest = z
    .object({
    name: z.string().min(1).max(200),
    type: SkuType,
    competitorId: z.string().uuid().nullable(),
    // Not nullable inside the partial: omitting it leaves the category alone,
    // but there is no way to clear one, because the column is NOT NULL.
    categoryId: z.string().uuid(),
    // Same rule, same reason — `skus.grouping_id` is NOT NULL since 0054.
    groupingId: z.string().uuid(),
    targetPriceIdr: RupiahAmount.nullable(),
    division: SkuDivision.nullable(),
})
    .partial();
/**
 * Create a region — a sub-distributor.
 *
 * `subdistCode` IS THE KEY. It is KODESUBDIST from the distributor's own files,
 * it is unique (`regions_subdist_code_key`), and it is what every bulk importer
 * resolves a region by. There is no second identifier: the derived `code`
 * (`900024-BANDUNG`) that this API invented was retired along with the
 * (sub-distributor, area) pair it encoded.
 *
 * `area` is descriptive — the patch of ground this sub-distributor works, KOTA
 * upstream. It names the region on screen but identifies nothing.
 */
export const UpsertRegionRequest = z.object({
    subdistCode: z.string().min(1).max(32),
    subdistName: z.string().min(1).max(200),
    area: z.string().min(1).max(200),
    /**
     * Refuse a check-in measured past the outlet's tolerance, rather than record
     * it with a warning — `regions.enforce_gps_tolerance`. Optional on create so
     * the column default (off) applies; the importer never sends it.
     */
    enforceGpsTolerance: z.boolean().optional(),
});
/**
 * `subdistCode` is absent for the reason it is absent from `UpdateOutletRequest`:
 * it is what the bulk importer resolves a region by, so renaming it would turn
 * the next import of the old code into a second region — and a second region is
 * a second scope, which quietly halves what everyone entitled to the first can
 * see. Retiring a region means creating its replacement and deactivating it.
 *
 * Nothing in the database stops the rename — outlets, entitlements and targets
 * all point at `regions.id` — which is exactly what makes it dangerous. The
 * damage lands on the next import, not on the edit.
 */
export const UpdateRegionRequest = z
    .object({
    subdistName: z.string().min(1).max(200),
    area: z.string().min(1).max(200),
    // Two states, like `haveChiller` on the outlet: absent leaves the switch
    // alone, `true`/`false` sets it. The column is NOT NULL, so `false` IS the
    // off position and a caller who means it sends it.
    enforceGpsTolerance: z.boolean(),
})
    .partial();
export const UpsertCompetitorRequest = z.object({ name: z.string().min(1).max(200) });
export const UpsertSkuCategoryRequest = z.object({ name: z.string().min(1).max(100) });
/**
 * FR-12 SKU grouping master — the SECOND classification axis on the catalogue,
 * added by migration 0054.
 *
 * NOT A RENAME OF `SkuCategory` AND NOT A PARENT OF IT. A category says what
 * kind of product something is ("Kopi RTD"); a grouping is the commercial cut
 * the business reports on. The two do not nest in either direction — one
 * grouping spans several categories and one category turns up under several
 * groupings — which is exactly why this is its own master rather than a second
 * field on the category row. Anything that treats the pair as a hierarchy is
 * wrong about the data.
 *
 * CODE AND NAME, like `Channel` and unlike `SkuCategory`. The argument against a
 * code for categories was that nothing outside this system quoted a category
 * identifier. A grouping is quoted: the SKU export writes it, an operator edits
 * that file, and the bulk importer resolves by it. The code is a real external
 * key rather than an invented second one, and it is what makes a rename safe,
 * because nothing downstream is keyed on the name.
 */
export const SkuGrouping = z.object({
    id: Id,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(100),
    active: z.boolean(),
});
/**
 * The absorber grouping migration 0054 seeds, so `skus.grouping_id` could be
 * `NOT NULL` on a catalogue that predates the master.
 *
 * A REAL ROW, NOT A NULL, and that is what makes the column non-nullable and
 * every join to it an inner one. The consequence is that "this SKU is in no
 * grouping" arrives as a name rather than an absence, and a report that prints
 * it verbatim shows "Tanpa Grouping" in a column of commercial cuts — which
 * reads as a grouping somebody created. Exports render it as `-` instead.
 *
 * Matched on the CODE, never the name: the name is editable through
 * `UpdateSkuGroupingRequest` and the code is not.
 */
export const SKU_GROUPING_NONE_CODE = 'TANPA-GROUPING';
/**
 * `code` is present on create and absent from the update request below, like
 * every other natural key here: the SKU importer's grouping column resolves
 * against the code, and renaming it would strand every file already carrying the
 * old one.
 */
export const CreateSkuGroupingRequest = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(100),
});
export const UpdateSkuGroupingRequest = z.object({ name: z.string().min(1).max(100) });
/**
 * FR-12 outlet channel master — GT, MT, HORECA and whatever else the network
 * grows.
 *
 * `Outlet.channel` was a free string until migration 0044, so the channel axis
 * of every report was whatever operators had typed, and the picker offering the
 * values was a SELECT DISTINCT over the outlet master. Same failure as the SKU
 * category before it: three spellings of one channel are three rows in a GROUP
 * BY, and `/reporting/planogram/by-channel` reports each third as a small one.
 *
 * CODE AND NAME, unlike `SkuCategory`. The argument against a code there was
 * that nothing outside the system quoted a category identifier. A channel is
 * quoted constantly — it arrives in the distributor's outlet files, the
 * round-trip export writes it, and the bulk importer resolves by it — so the
 * code is a real external key rather than an invented second one. It is also
 * what makes a rename safe, because nothing downstream is keyed on the name.
 */
export const Channel = z.object({
    id: Id,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(100),
    active: z.boolean(),
});
/**
 * FR-12 outlet classification — a sub-type OF A CHANNEL.
 *
 * Not a second free axis: one channel has one to many classifications, an outlet
 * carries exactly one of each, and the two are consistent by construction rather
 * than by convention (a composite foreign key; see the `outlets` schema). So
 * "Reguler" under GT and "Reguler" under MT are different rows describing
 * different things, which is why `name` is unique per channel and `code`
 * globally.
 *
 * The global code is what lets the bulk importer resolve a classification on its
 * own and then CHECK that the channel hanging off it matches the channel the
 * same spreadsheet row named — the cross-check that catches a mis-mapped pair of
 * columns before it becomes four hundred wrong outlets.
 */
export const OutletClassification = z.object({
    id: Id,
    channelId: Id,
    channelName: z.string(),
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(100),
    active: z.boolean(),
});
/**
 * `code` is present on create and absent from the update request below, like
 * every other natural key here: the importer's upsert target is the code, and
 * renaming it would strand every row the last import matched.
 */
export const CreateChannelRequest = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(100),
});
export const UpdateChannelRequest = z.object({ name: z.string().min(1).max(100) });
export const CreateOutletClassificationRequest = z.object({
    /**
     * Fixed at creation and absent from the update request, which is a stronger
     * rule than `code`'s and deliberately so. Re-parenting a classification would
     * silently move every outlet already carrying it into a different channel —
     * rewriting history in every report grouped on the pair, with no record that
     * it happened. A classification under the wrong channel is retired and
     * recreated, which leaves the outlets where they are until somebody moves them
     * on purpose.
     */
    channelId: z.string().uuid(),
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(100),
});
export const UpdateOutletClassificationRequest = z.object({ name: z.string().min(1).max(100) });
/**
 * Activate or deactivate a master-data row.
 *
 * There is no delete anywhere in this API, and this is the reason: visits,
 * photos and scores reference master data, so removing a row would either
 * orphan history or cascade into it. Deactivation is the retirement mechanism —
 * the row stops appearing in pickers and keeps answering for everything already
 * recorded against it.
 */
export const SetActiveRequest = z.object({ active: z.boolean() });
/* -------------------------------------------------------------------------- */
/* FR-14 visit targets — the Plan half of Plan vs Actual                      */
/* -------------------------------------------------------------------------- */
/**
 * A plan period: always the first day of a month.
 *
 * A `date` pinned to day 1 rather than a `'2026-01'` string so that `BETWEEN`,
 * `generate_series` and the unique index all work without parsing text. The
 * database carries the same rule as a CHECK; this rejects it one layer earlier,
 * where the message can name the field.
 */
export const PeriodMonth = z
    .string()
    .regex(/^\d{4}-\d{2}-01$/, 'periode harus tanggal 1, misalnya 2026-08-01');
/**
 * Create or replace one salesman's plan for one month.
 *
 * Upserts on `(salesUserId, periodMonth)`. `regionId` is carried explicitly
 * rather than derived from the user because a salesman's entitlement is a SET of
 * regions — there is no single region to look up — and because the row must hold
 * its own `region_id` for the mandatory scope predicate (ADR-0003). The API
 * rejects a combination the salesman is not entitled to, so the two cannot
 * silently disagree.
 */
export const UpsertVisitTargetRequest = z.object({
    regionId: z.string().uuid(),
    salesUserId: z.string().uuid(),
    periodMonth: PeriodMonth,
    /** Zero is "retired", not "must make no visits" — see `UpdateVisitTargetRequest`. */
    targetVisits: z.number().int().nonnegative(),
    note: z.string().max(500).nullable().default(null),
});
/**
 * Partial update by id.
 *
 * `salesUserId` and `periodMonth` are absent for the same reason `code` is
 * absent from `UpdateOutletRequest`: together they are the natural key the
 * upsert conflicts on, so moving a plan to a different person or month is
 * creating a different plan, not editing this one.
 *
 * There is no deactivate route either. Retiring a plan is `targetVisits: 0`, and
 * every report reads a zero target as "no attainment percentage" rather than as
 * 0% — the distinction matters, because 0% looks like failure and "no target"
 * looks like what it is.
 */
export const UpdateVisitTargetRequest = z
    .object({
    targetVisits: z.number().int().nonnegative(),
    note: z.string().max(500).nullable(),
})
    .partial();
/**
 * The bulk path.
 *
 * Planning is done for a team over a year at a time: forty salesmen by twelve
 * months is 480 requests through the single-row route, which is a spreadsheet
 * paste that takes a minute and fails halfway. One transaction, all or nothing.
 */
export const UpsertVisitTargetsRequest = z.object({
    targets: z.array(UpsertVisitTargetRequest).min(1).max(500),
});
/* -------------------------------------------------------------------------- */
/* FR-14 competition plans — the Plan half of the competitive price index     */
/* -------------------------------------------------------------------------- */
/**
 * How expensive OUR product is expected to be relative to a competitor's.
 *
 * A MULTIPLIER, not a percentage: 0.9 means "our product should cost 0.9× the
 * competitor's", i.e. we plan to undercut them by 10%. 90 would be read as
 * ninety times, which is why the ceiling below is 10 and the form states the
 * unit under the field.
 *
 * OURS OVER THEIRS, AND IT USED TO BE THE OTHER WAY ROUND. Migration 0010
 * introduced this column as theirs-over-ours; migration 0024 inverted it, so a
 * plan that read 1.2500 there reads 0.8000 here. The direction matters more than
 * the number: LOWER IS BETTER, because lower means we are cheaper than the rival
 * we benchmarked against. Every grade, badge tone and sort in the price-index
 * report depends on that reading — see `CompetitionGrade` in `settings.ts`.
 *
 * THIS IS THE WHOLE PRICE INDEX NOW. It used to be one of two: `Sku.targetPriceIdr`
 * carried our own list price and a second index divided an observed price by it.
 * That metric is retired. This one compares two OBSERVED prices to each other and
 * never reads a list price at all.
 *
 * Bounded for the reason `RupiahAmount` gives — without a ceiling the request
 * validates and PostgreSQL answers "numeric field overflow", a 500 for a typo.
 * The database carries the same bound as a CHECK.
 *
 * Four decimals, checked here rather than left to the column: `numeric(6,4)`
 * rounds silently, and a plan whose target is not the number the admin typed is
 * worse than a rejected form.
 */
export const TargetIndex = z
    .number()
    .positive()
    .max(10)
    .refine((value) => Number(value.toFixed(4)) === value, {
    message: 'indeks target maksimal empat angka di belakang koma',
});
/**
 * One benchmark: our SKU against another, and where we expect our price to sit
 * relative to it.
 *
 * TWO KINDS OF PLAN, and `pairKind` is which. 'KOMPETITOR' compares us to a
 * rival's product — the original FR-14 benchmark. 'INTERNAL' compares two of OUR
 * OWN SKUs, the price ladder ("our 600ml should sell at 0.8x our 1500ml"), which
 * migration 0057 made expressible. They share one table, one formula and one
 * grading rule; only the left-hand side is required to be an INTERNAL SKU.
 *
 * Global — no region, no period. Many-to-many and free: one of our SKUs may be
 * benchmarked against several others and vice versa, and only the exact pair is
 * unique.
 */
export const CompetitionPlan = z.object({
    id: Id,
    internalSkuId: Id,
    competitorSkuId: Id,
    /**
     * The KIND of comparison, which is the TYPE of the SKU on the comparison side.
     *
     * DERIVED, NEVER SENT. It is a property of `competitorSkuId` — the server reads
     * it off the SKU it looks up — so neither write request carries it. A client
     * that sent it could only ever disagree with the catalogue, and the composite
     * foreign key would reject the disagreement anyway.
     */
    pairKind: SkuType,
    targetIndex: TargetIndex,
    note: z.string().max(500).nullable(),
    active: z.boolean(),
});
/**
 * Create a plan. Conflicts on `(internalSkuId, competitorSkuId)`.
 *
 * THE FIRST SIDE must be an INTERNAL SKU. The second may be either kind: a
 * KOMPETITOR SKU for a competitor benchmark, or another INTERNAL SKU for a price
 * ladder. That is a rule about rows in another table, so it is not expressible
 * here — the repository checks it and the composite foreign key enforces it.
 *
 * THE TWO SIDES MUST DIFFER, which is likewise not expressible here and is
 * `competition_plans_distinct_sides` in the database (0057). Until an INTERNAL
 * SKU could sit on the second side it was impossible by construction; now it is
 * a constraint, because a self-paired plan reports a permanent index of 1.0000
 * and looks like a product priced exactly on plan.
 *
 * `pairKind` IS ABSENT ON PURPOSE. It follows from `competitorSkuId`, and a
 * client that could state it separately could state it wrongly.
 */
export const UpsertCompetitionPlanRequest = z.object({
    internalSkuId: z.string().uuid(),
    competitorSkuId: z.string().uuid(),
    targetIndex: TargetIndex,
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * Partial update, THE PAIR INCLUDED.
 *
 * Unlike `UpdateSkuRequest` and `UpdateVisitTargetRequest`, whose natural keys
 * are deliberately omitted, both SKU sides are editable here. Repointing a plan
 * is how an operator corrects a benchmark they set against the wrong variant,
 * and there is no importer upserting on the pair for a rename to fool. The cost
 * is that a plan silently changes what it measured; the audit log records the
 * change, and the report always renders the pair beside the number so a reader
 * is never looking at a bare index whose subject they have to assume.
 */
export const UpdateCompetitionPlanRequest = z
    .object({
    internalSkuId: z.string().uuid(),
    competitorSkuId: z.string().uuid(),
    targetIndex: TargetIndex,
    note: z.string().trim().max(500).nullable(),
})
    .partial();
//# sourceMappingURL=master.js.map